import { Link, Navigate } from 'react-router-dom';
import { PortalFrame, SellerTeamLabel } from '@/components/Layout';
import { useSession } from '@/api/hooks';
import { isSellerTeamOnly, sellerRooms } from '@/config';
import { ApiError, Card, CardContent, CardHeader, CardTitle, HelpTip, Icon } from '@dataroom/shared';

/**
 * "Your data rooms" — the seller portal's home. Lists every room the signed-in
 * actor works on the seller side of — as the seller, or on the seller's team
 * (labelled) — by name, and links into each. The single landing every
 * room-scoped view returns to (the in-room header's "← Your rooms" link), so a
 * seller in more than one deal is never stranded in one room.
 */
export function RoomsLandingPage() {
  const session = useSession();

  if (session.error instanceof ApiError && session.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  if (session.isPending) {
    return <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">Loading…</p>;
  }

  const rooms = sellerRooms(session.data);

  return (
    <PortalFrame>
      <main className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
        <div className="mb-1 flex items-center gap-2">
          <h1 className="font-serif text-2xl font-semibold leading-8 tracking-tight md:text-[28px] md:leading-9">
            Your data rooms
          </h1>
          <HelpTip id="seller.rooms.list" />
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Select a data room to submit documents, work the requested-documents checklist, and respond
          to LOIs.
        </p>
        {rooms.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>No data room yet</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                You aren&apos;t set up as the seller for a data room yet. Ask the deal intermediary to
                invite you, then sign in again.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            {rooms.map((room) => (
              <li key={room.roomId}>
                <Link
                  to={`/rooms/${room.roomId}`}
                  className="flex min-h-tap items-center justify-between gap-3 rounded-lg border bg-card p-4 shadow-sm transition-colors hover:bg-accent"
                >
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="min-w-0 font-semibold">{room.name}</span>
                    {isSellerTeamOnly(room) ? <SellerTeamLabel /> : null}
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-sm text-ink-2">
                    Open <Icon name="chevronRight" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </PortalFrame>
  );
}
